import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { CLAUDE_UNAVAILABLE, CURSOR_WAITS, DRAFT_TASK_NOTE, TASKS_PROMISE } from "../copy/journeyCopy";
import { taskMatchesFilter, taskOriginLabel, taskStage } from "../dashboard/dashboardModel";
import InlineAlert from "../ui/InlineAlert";
import Skeleton from "../ui/Skeleton";

const FILTERS = [
  ["needs-me", "Needs me"],
  ["draft", "Draft"],
  ["planning", "Planning"],
  ["ready", "Ready"],
  ["building", "Building"],
  ["delivered", "Delivered"],
  ["verifying", "Verifying"],
  ["changes-requested", "Changes requested"],
  ["accepted", "Accepted"],
  ["archived", "Archived"],
];

function planRepoId(plan) {
  const value = plan?.repoId;
  if (value && typeof value === "object") return String(value._id || value.id || "");
  return String(value || "");
}

export function TasksPage({ filter = "needs-me", repositoryId = "", repositoryIds = [], onFilter, onNewTask }) {
  const [plans, setPlans] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .get("/feature-plans", { params: { limit: 50 } })
      .then((response) => {
        if (!cancelled) setPlans(response.data?.plans || []);
      })
      .catch(() => {
        if (!cancelled) setError("Tasks could not be loaded.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const allowedIds = repositoryIds.length ? repositoryIds.map(String) : repositoryId ? [String(repositoryId)] : [];
  const visible = (plans || []).filter((plan) => (!allowedIds.length || allowedIds.includes(planRepoId(plan))) && taskMatchesFilter(plan, filter));

  return (
    <div className="rr-page" data-testid="tasks-inbox">
      <header className="rr-page-head">
        <div>
          <h1>Tasks</h1>
          <p>{TASKS_PROMISE}</p>
        </div>
        <div className="rr-actions">
          <button type="button" className="rr-button" onClick={onNewTask}>New Task</button>
        </div>
      </header>
      {error ? <InlineAlert tone="alert">{error}</InlineAlert> : null}
      <div className="rr-filters" role="tablist" aria-label="Task views">
        {FILTERS.map(([id, label]) => (
          <button key={id} type="button" className="rr-chip" aria-pressed={filter === id} onClick={() => onFilter?.(id)}>
            {label}
          </button>
        ))}
      </div>
      {plans === null ? <Skeleton label="Loading tasks" /> : null}
      {plans && !visible.length ? (
        <div className="rr-empty">
          <p><strong>No tasks in this view.</strong> Create a Task when you want work to happen.</p>
        </div>
      ) : null}
      {visible.length ? (
        <div className="rr-panel"><div className="rr-table">
          <div className="rr-table-head">
            <span>Task</span><span>Stage</span><span>Origin</span><span>Updated</span><span>Next</span>
          </div>
          {visible.map((plan) => (
            <Link key={plan._id} className="rr-table-row" to={`/tasks/${plan._id}`}>
              <strong>{plan.title || plan.featureRequestText || "Task"}</strong>
              <span>{taskStage(plan)}</span>
              <span>{taskOriginLabel(plan)}</span>
              <span>{plan.deliveredWork?.headSha ? String(plan.deliveredWork.headSha).slice(0, 7) : "—"}</span>
              <span className="rr-open">Open</span>
            </Link>
          ))}
        </div></div>
      ) : null}
    </div>
  );
}

export function NewTaskPage({ repositories = [], repositoryId = "", guideId = "", mutateAllowed = false, onCreated }) {
  const [repoId, setRepoId] = useState(repositoryId || repositories[0]?._id || "");
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [nonGoals, setNonGoals] = useState("");
  const [guides, setGuides] = useState([]);
  const [attached, setAttached] = useState(guideId ? [guideId] : []);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!repoId) return undefined;
    let cancelled = false;
    api.listCodebaseGuide(repoId, { activeOnly: true }).then((response) => {
      if (!cancelled) setGuides(response.data?.entries || []);
    }).catch(() => {
      if (!cancelled) setGuides([]);
    });
    return () => {
      cancelled = true;
    };
  }, [repoId]);

  async function save(event) {
    event.preventDefault();
    setError("");
    try {
      const response = await api.post("/task-drafts", {
        repoId,
        desiredOutcome: outcome,
        notes,
        nonGoals,
        guideIds: attached,
      });
      onCreated?.(response.data?.task);
    } catch (err) {
      setError(err?.response?.data?.error || "The Task draft could not be saved.");
    }
  }

  return (
    <form className="rr-stack rr-health" onSubmit={save}>
      <header className="rr-page-header">
        <p className="rr-kicker">Tasks</p>
        <h1>New Task</h1>
        <p>{DRAFT_TASK_NOTE}</p>
      </header>
      {error ? <InlineAlert tone="alert">{error}</InlineAlert> : null}
      <label>
        Repository
        <select value={repoId} onChange={(event) => setRepoId(event.target.value)}>
          {repositories.map((repo) => <option key={repo._id} value={repo._id}>{repo.owner}/{repo.name}</option>)}
        </select>
      </label>
      <label>
        Desired outcome
        <textarea value={outcome} onChange={(event) => setOutcome(event.target.value)} rows={4} required />
      </label>
      <label>
        Notes
        <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} />
      </label>
      <label>
        Non-goals
        <textarea value={nonGoals} onChange={(event) => setNonGoals(event.target.value)} rows={2} />
      </label>
      <fieldset>
        <legend>Guides</legend>
        {guides.map((guide) => (
          <label key={guide._id}>
            <input
              type="checkbox"
              checked={attached.includes(guide._id)}
              onChange={(event) => {
                setAttached((current) => event.target.checked ? [...current, guide._id] : current.filter((id) => id !== guide._id));
              }}
            />
            {guide.title}
          </label>
        ))}
        {!guides.length ? <p>No active Guides for this repository.</p> : null}
      </fieldset>
      <button type="submit" className="rr-button" disabled={!mutateAllowed}>Save draft</button>
    </form>
  );
}

export function TaskDetailPage({ taskId, section = "overview" }) {
  const [task, setTask] = useState(null);
  const [agents, setAgents] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get(`/feature-plans/${taskId}`),
      api.get("/workspace/agents").catch(() => ({ data: { providers: [] } })),
    ]).then(([planResponse, agentResponse]) => {
      if (cancelled) return;
      const plan = planResponse.data?.plan || planResponse.data;
      setTask(plan);
      setAgents(agentResponse.data?.providers || []);
    }).catch(() => {
      if (!cancelled) setError("That Task is not available in this workspace.");
    });
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  if (error) return <InlineAlert tone="alert">{error}</InlineAlert>;
  if (!task) return <Skeleton label="Loading task" />;
  const origin = task.customerOrigin || {};
  const brief = [
    `# ${task.title || "Task"}`,
    "",
    "## Desired outcome",
    task.featureRequestText || "",
    "",
    "## Guides",
    origin.guideIds?.length ? origin.guideIds.join(", ") : "No Guides attached.",
    "",
    "## Requirements",
    (task.draftContext?.acceptanceCriteria || []).map((item) => `- ${item}`).join("\n") || "- Confirm the desired outcome before implementation.",
    "",
    "## Scope",
    task.draftContext?.nonGoals || "Non-goals have not been written yet.",
    "",
    "## Base",
    origin.reviewedHeadSha ? `Reviewed commit ${origin.reviewedHeadSha}.` : "No base commit is pinned yet.",
    "",
    "## Tests",
    "State the tests that must pass before this Task is delivered.",
    "",
    "This brief does not authorize an agent.",
  ].join("\n");
  const repo = task.repoId?._id || task.repoId;

  return (
    <article className="rr-stack rr-health" data-testid="task-detail">
      <header className="rr-page-header">
        <p className="rr-kicker">Task · {taskStage(task)}</p>
        <h1>{task.title || task.featureRequestText || "Task"}</h1>
        <p>{DRAFT_TASK_NOTE}</p>
      </header>
      <p>Origin: {taskOriginLabel(task)}</p>
      <nav className="rr-filters" aria-label="Task sections">
        {["overview", "context", "plan", "build", "verify"].map((id) => (
          <Link key={id} to={`/tasks/${task._id || taskId}/${id === "overview" ? "" : id}`.replace(/\/$/, "")} aria-current={section === id ? "page" : undefined}>
            {id[0].toUpperCase() + id.slice(1)}
          </Link>
        ))}
      </nav>
      {origin.reviewId ? <p><Link to={`/reviews/${origin.reviewId}`}>Open the source Decision Review</Link></p> : null}
      {origin.reviewedHeadSha ? <p>Reviewed commit <span className="rr-sha">{String(origin.reviewedHeadSha).slice(0, 7)}</span></p> : null}
      <h2>Request</h2>
      <p>{task.featureRequestText}</p>
      <h2>Build</h2>
      <p>{CURSOR_WAITS}</p>
      <p>{agents?.find((item) => item.provider === "claude_code")?.detail || CLAUDE_UNAVAILABLE}</p>
      <div className="rr-actions">
        <button type="button" className="rr-button" disabled>Send to Cursor</button>
        <button type="button" className="rr-button secondary" disabled>Send to Claude Code</button>
        <button
          type="button"
          className="rr-button secondary"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(brief);
            } catch {
              // The brief remains visible below when clipboard access is unavailable.
            }
            setCopied(true);
          }}
        >
          Copy implementation brief
        </button>
      </div>
      {copied ? <p>Copied. No agent attempt was created.</p> : null}
      <pre data-testid="task-brief">{brief}</pre>
      {repo ? <p><Link to={`/repositories/${repo}/tasks/${task._id || taskId}`}>Open the full task workspace</Link></p> : null}
    </article>
  );
}
